import { Card, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChevronDown, ChevronRight, ExternalLink, HelpCircle } from 'lucide-react';
import type { AlertInfo, ProductData } from '../models/types';
import { detectAlerts, formatDecisionAge, selectMatchesInUse } from '../models/types';
import { AlertBadge } from './AlertBadge';

interface ProductCardProps {
  readonly product: ProductData;
  readonly isSelected: boolean;
  readonly index: number;
  readonly competitorCount: number;
  readonly onSelect: () => void;
  readonly isVariation?: boolean;
  readonly isExpanded?: boolean;
  readonly onToggleExpand?: () => void;
}

function renderReviewBadge(product: ProductData, hasCompetitorProducts: boolean, alerts: AlertInfo[]) {
  if (product.humanVerifiedNoCompetitor) {
    return (
      <Badge bg="info" style={{ fontSize: '0.7rem' }}>
        Exclusive
      </Badge>
    );
  }

  if (!hasCompetitorProducts) {
    return (
      <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
        No matches
      </Badge>
    );
  }

  return <AlertBadge alerts={alerts} size="sm" />;
}

function renderReviewerDecision(product: ProductData) {
  if (!product.humanDecidedBy && !product.humanDecidedAt) {
    return null;
  }

  const decisionAge = product.humanDecidedAt ? formatDecisionAge(product.humanDecidedAt) : '';

  return (
    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
      Actioned{product.humanDecidedBy ? ` by ${product.humanDecidedBy}` : ''}
      {decisionAge ? `, ${decisionAge}` : ''}
    </div>
  );
}

export function ProductCard({
  product,
  isSelected,
  index,
  competitorCount,
  onSelect,
  isVariation = false,
  isExpanded = false,
  onToggleExpand,
}: ProductCardProps) {
  const skinSeoul = product.skinSeoulProduct;
  const hasVariations = !isVariation && skinSeoul.type === 'Variable' && (skinSeoul.variations?.length || 0) > 0;

  const matchesInUse = selectMatchesInUse(product);
  const allAlerts = matchesInUse.flatMap((comp) =>
    comp.competitorProducts.flatMap((cp) => detectAlerts(skinSeoul, cp as any)),
  );

  const uniqueAlerts = allAlerts.filter((alert, idx, arr) => arr.findIndex((a) => a.type === alert.type) === idx);
  const hasCompetitorProducts = matchesInUse.some((comp) => comp.competitorProducts.length > 0);

  const variationCount = skinSeoul.variations?.length || 0;

  return (
    <Card
      className={`mb-2 border-0 ${isSelected ? 'border-primary border-2' : ''}`}
      onClick={onSelect}
      style={{
        cursor: 'pointer',
        backgroundColor: isSelected ? '#f0f7ff' : isVariation ? '#fafafa' : 'white',
        transition: 'all 0.2s ease',
        borderRadius: '8px',
        boxShadow: isVariation ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
        marginLeft: isVariation ? '24px' : 0,
        borderLeft: isVariation ? '2px solid #dee2e6' : 'none',
      }}
    >
      <Card.Body className="p-2">
        <div className="d-flex gap-2">
          {hasVariations && (
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: '20px', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand?.();
              }}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </div>
          )}
          {!isVariation && (
            <div className="position-relative" style={{ minWidth: '60px' }}>
              {skinSeoul.productImageUrls && skinSeoul.productImageUrls.length > 0 ? (
                <img
                  src={skinSeoul.productImageUrls[0]}
                  alt={skinSeoul.name}
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'contain',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center text-muted"
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '0.65rem',
                  }}
                >
                  No img
                </div>
              )}
              <Badge
                bg="dark"
                className="position-absolute"
                style={{
                  top: '-6px',
                  left: '-6px',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '0.65rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {index + 1}
              </Badge>
            </div>
          )}

          <div className="flex-grow-1 min-width-0">
            <div className="d-flex justify-content-between align-items-start mb-1">
              <h6
                className="mb-0 text-truncate"
                style={{ fontSize: isVariation ? '0.8rem' : '0.85rem', lineHeight: '1.3', maxWidth: '250px' }}
                title={skinSeoul.name}
              >
                {isVariation && <span className="text-muted me-1">└</span>}
                {skinSeoul.name}
              </h6>
              <div className="d-flex gap-1">
                {!isVariation && variationCount > 0 && (
                  <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                    {variationCount} var
                  </Badge>
                )}
                {competitorCount > 0 && (
                  <Badge bg="info" style={{ fontSize: '0.65rem', flexShrink: 0 }}>
                    {competitorCount} competitor{competitorCount !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap mb-1 align-items-center">
              <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                SKU: {skinSeoul.sku}
              </small>
              {!isVariation && (
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                  {skinSeoul.brand}
                </small>
              )}
              {skinSeoul.productLink && (
                <a
                  href={skinSeoul.productLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="text-primary d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '0.7rem' }}
                  title="Open this product on skin-seoul.com"
                >
                  View in SkinSeoul
                  <ExternalLink size={10} />
                </a>
              )}
            </div>

            <div className="d-flex gap-2 flex-wrap mb-1 align-items-center">
              {skinSeoul.salePrice != null && (
                <small className="fw-bold" style={{ fontSize: '0.75rem', color: '#0d6efd' }}>
                  ${skinSeoul.salePrice.toFixed(2)}
                </small>
              )}
              {skinSeoul.salePrice != null && skinSeoul.originalPrice != null && skinSeoul.originalPrice !== skinSeoul.salePrice && (
                <small className="text-decoration-line-through text-muted" style={{ fontSize: '0.7rem' }}>
                  ${skinSeoul.originalPrice.toFixed(2)}
                </small>
              )}
              {skinSeoul.salePrice == null && skinSeoul.originalPrice != null && (
                <small className="fw-bold" style={{ fontSize: '0.75rem', color: '#0d6efd' }}>
                  ${skinSeoul.originalPrice.toFixed(2)}
                </small>
              )}
              {skinSeoul.salePrice != null && skinSeoul.originalPrice != null && skinSeoul.originalPrice !== skinSeoul.salePrice && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <div className="text-start">
                        <strong>Pricing Information</strong>
                        <div className="mt-1">
                          • <strong>Sale Price:</strong> ${skinSeoul.salePrice.toFixed(2)} (current selling price)
                        </div>
                        <div>
                          • <strong>Original Price:</strong> <span className="text-decoration-line-through">${skinSeoul.originalPrice.toFixed(2)}</span> (regular price before discount)
                        </div>
                        <div className="mt-1 text-success">
                          <strong>Savings:</strong> ${(skinSeoul.originalPrice - skinSeoul.salePrice).toFixed(2)} ({(((skinSeoul.originalPrice - skinSeoul.salePrice) / skinSeoul.originalPrice) * 100).toFixed(0)}% off)
                        </div>
                      </div>
                    </Tooltip>
                  }
                >
                  <HelpCircle
                    size={12}
                    className="text-muted"
                    style={{ cursor: 'pointer' }}
                  />
                </OverlayTrigger>
              )}
              {skinSeoul.cogs != null && (
                <small style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                  COGS: ₩{skinSeoul.cogs.toLocaleString()}
                </small>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-1 flex-wrap">
                {skinSeoul.type && !isVariation && (
                  <Badge
                    bg={skinSeoul.type === 'Simple' ? 'success' : 'warning'}
                    style={{ fontSize: '0.6rem' }}
                  >
                    {skinSeoul.type}
                  </Badge>
                )}
                {skinSeoul.size && (
                  <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                    {skinSeoul.size}
                  </Badge>
                )}
                {skinSeoul.pack && (
                  <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                    {skinSeoul.pack}
                  </Badge>
                )}
              </div>
              {renderReviewBadge(product, hasCompetitorProducts, uniqueAlerts)}
            </div>

            {renderReviewerDecision(product)}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
